import { apiFetch } from "./api.js";

const STORAGE_PREFIX = "nitro_simulator_chat_intent_v1";
const LOCK_PREFIX = "nitro:simulator-chat-intent:v1";
const VALID_REQUEST_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const VALID_DIGEST = /^[a-f0-9]{64}$/;
const VALID_MESSAGE_ID = /^[0-9]{1,25}$/;
const VALID_SIMULATOR_SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/;
const OPERATION_STATUSES = new Set([
  "planned",
  "processing",
  "reconciling",
  "uncertain",
  "unknown",
  "completed",
  "failed",
]);
const BACKEND_STATUSES = new Set([
  "planned",
  "processing",
  "reconciling",
  "uncertain",
  "completed",
  "failed",
]);

export async function createSimulatorChatIntent({ actorId, simulatorSlug, message }, options = {}) {
  const cryptoApi = cryptoFor(options);
  const clientRequestId = cryptoApi.randomUUID();
  if (!VALID_REQUEST_ID.test(clientRequestId)) {
    throw chatIntentError("CHAT_INTENT_KEY_INVALID", "No fue posible preparar una identidad segura para el mensaje.");
  }
  const actorBinding = await simulatorChatActorBinding(actorId, { cryptoApi });
  const normalizedSimulatorSlug = normalizeSimulatorSlug(simulatorSlug);
  const normalizedMessage = normalizeSimulatorChatText(message);
  const now = validNow(options.now?.() || new Date());
  return Object.freeze({
    v: 1,
    actorBinding,
    simulatorSlug: normalizedSimulatorSlug,
    clientRequestId,
    contentDigest: await simulatorChatContentDigest({
      actorBinding,
      simulatorSlug: normalizedSimulatorSlug,
      clientRequestId,
      message: normalizedMessage,
    }, { cryptoApi }),
    status: "planned",
    messageId: null,
    errorCode: null,
    createdAt: now,
    updatedAt: now,
  });
}

export async function claimSimulatorChatIntent(payload, options = {}) {
  const actorBinding = await simulatorChatActorBinding(payload.actorId, options);
  const simulatorSlug = normalizeSimulatorSlug(payload.simulatorSlug);
  const storage = storageFor(options);
  const storageKey = chatStorageKey(actorBinding, simulatorSlug);
  return withSimulatorChatLock(actorBinding, simulatorSlug, options, null, async () => {
    const existing = readStoredIntent(storage, storageKey, actorBinding, simulatorSlug);
    if (existing) return { intent: Object.freeze({ ...existing }), created: false };
    const legacy = readOptionalStoredIntent(legacyStorageFor(options), storageKey, actorBinding, simulatorSlug);
    if (legacy) {
      writeStoredIntent(storage, storageKey, legacy);
      return { intent: Object.freeze({ ...legacy }), created: false };
    }
    const candidate = await createSimulatorChatIntent({ ...payload, simulatorSlug }, options);
    if (typeof options.guard === "function" && !options.guard()) {
      throw chatIntentError("CHAT_INTENT_CHANGED", "La identidad del mensaje cambió durante la preparación.");
    }
    const current = readStoredIntent(storage, storageKey, actorBinding, simulatorSlug);
    if (current) return { intent: Object.freeze({ ...current }), created: false };
    const safeIntent = storedIntent(candidate);
    writeStoredIntent(storage, storageKey, safeIntent);
    return { intent: Object.freeze(safeIntent), created: true };
  });
}

export async function loadSimulatorChatIntent(actorId, simulatorSlug, options = {}) {
  const actorBinding = await simulatorChatActorBinding(actorId, options);
  const normalizedSimulatorSlug = normalizeSimulatorSlug(simulatorSlug);
  const storage = storageFor(options);
  const storageKey = chatStorageKey(actorBinding, normalizedSimulatorSlug);
  // Read only after the asynchronous identity binding is complete. This prevents
  // an older actor/simulator load from crossing a newer component generation.
  const current = readStoredIntent(storage, storageKey, actorBinding, normalizedSimulatorSlug);
  if (current) return Object.freeze({ ...current });
  const legacy = readOptionalStoredIntent(legacyStorageFor(options), storageKey, actorBinding, normalizedSimulatorSlug);
  return legacy ? Object.freeze({ ...legacy }) : null;
}

export async function saveSimulatorChatIntent(actorId, intent, options = {}) {
  if (!isStoredIntent(intent)) {
    throw chatIntentError("CHAT_INTENT_STORAGE_INVALID", "El estado local del mensaje no es válido.");
  }
  const actorBinding = await simulatorChatActorBinding(actorId, options);
  const normalizedSimulatorSlug = normalizeSimulatorSlug(intent.simulatorSlug);
  if (actorBinding !== intent.actorBinding) {
    throw chatIntentError("CHAT_INTENT_ACTOR_MISMATCH", "El mensaje pendiente pertenece a otra sesión.");
  }
  const storage = storageFor(options);
  const storageKey = chatStorageKey(actorBinding, normalizedSimulatorSlug);
  const safeIntent = storedIntent(intent);
  return withSimulatorChatLock(actorBinding, normalizedSimulatorSlug, options, null, () => {
    const current = readStoredIntent(storage, storageKey, actorBinding, normalizedSimulatorSlug);
    if (current ? current.clientRequestId !== intent.clientRequestId : false) return null;
    const legacy = current ? null : readOptionalStoredIntent(legacyStorageFor(options), storageKey, actorBinding, normalizedSimulatorSlug);
    if (legacy ? legacy.clientRequestId !== intent.clientRequestId : !current && options.createIfAbsent !== true) return null;
    writeStoredIntent(storage, storageKey, safeIntent);
    return Object.freeze(safeIntent);
  });
}

export async function clearSimulatorChatIntent(actorId, simulatorSlug, clientRequestId, options = {}) {
  if (!VALID_REQUEST_ID.test(String(clientRequestId || ""))) return false;
  const actorBinding = await simulatorChatActorBinding(actorId, options);
  const normalizedSimulatorSlug = normalizeSimulatorSlug(simulatorSlug);
  const storage = storageFor(options);
  const storageKey = chatStorageKey(actorBinding, normalizedSimulatorSlug);
  return withSimulatorChatLock(actorBinding, normalizedSimulatorSlug, options, false, () => {
    const current = readStoredIntent(storage, storageKey, actorBinding, normalizedSimulatorSlug);
    const legacyStorage = legacyStorageFor(options);
    const legacy = readOptionalStoredIntent(legacyStorage, storageKey, actorBinding, normalizedSimulatorSlug);
    if (current && current.clientRequestId !== clientRequestId) return false;
    if (legacy && legacy.clientRequestId !== clientRequestId) return false;
    if (!current && !legacy) return false;
    if (current) removeStoredIntent(storage, storageKey);
    if (legacy) removeStoredIntent(legacyStorage, storageKey);
    return true;
  });
}

export async function simulatorChatActorBinding(actorId, options = {}) {
  const normalized = String(actorId ?? "").trim();
  if (!normalized) throw chatIntentError("CHAT_ACTOR_REQUIRED", "Debes iniciar sesión para usar el chat.");
  return sha256(`nitro-simulator-chat-actor:v1:${normalized}`, cryptoFor(options));
}

export async function simulatorChatContentDigest(intent, options = {}) {
  const actorBinding = String(intent?.actorBinding || "");
  const clientRequestId = String(intent?.clientRequestId || "");
  if (!VALID_DIGEST.test(actorBinding) || !VALID_REQUEST_ID.test(clientRequestId)) {
    throw chatIntentError("CHAT_INTENT_IDENTITY_INVALID", "La identidad del mensaje no es válida.");
  }
  const simulatorSlug = normalizeSimulatorSlug(intent.simulatorSlug);
  const message = normalizeSimulatorChatText(intent.message);
  return sha256(
    `nitro-simulator-chat-content:v1:${clientRequestId}:${actorBinding}:${simulatorSlug}:${message}`,
    cryptoFor(options),
  );
}

export async function hasSimulatorChatDraftChanged(intent, message, options = {}) {
  if (!intent) return false;
  const digest = await simulatorChatContentDigest({
    actorBinding: intent.actorBinding,
    simulatorSlug: intent.simulatorSlug,
    clientRequestId: intent.clientRequestId,
    message,
  }, options);
  return digest !== intent.contentDigest;
}

export function updateSimulatorChatIntent(intent, outcome, options = {}) {
  if (!isStoredIntent(intent)) {
    throw chatIntentError("CHAT_INTENT_STORAGE_INVALID", "El estado local del mensaje no es válido.");
  }
  const now = validNow(options.now?.() || new Date());
  if (!outcome?.found) {
    return Object.freeze(storedIntent({ ...intent, status: "unknown", updatedAt: now }));
  }
  if (!BACKEND_STATUSES.has(outcome.status)) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro devolvió un estado de chat que no se pudo verificar.");
  }
  return Object.freeze(storedIntent({
    ...intent,
    status: outcome.status,
    messageId: outcome.messageId || null,
    errorCode: outcome.errorCode || null,
    updatedAt: now,
  }));
}

export async function inspectSimulatorChatIntent(simulatorSlug, clientRequestId, options = {}) {
  const request = options.request || apiFetch;
  const slug = normalizeSimulatorSlug(simulatorSlug);
  if (!VALID_REQUEST_ID.test(String(clientRequestId || ""))) {
    throw chatIntentError("CHAT_INTENT_KEY_INVALID", "La identidad del mensaje no es válida.");
  }
  let data;
  try {
    data = await request(
      `/api/v1/simulator-chat/${encodeURIComponent(slug)}/messages/${encodeURIComponent(clientRequestId)}/status`,
      { cache: "no-store", timeoutMs: options.timeoutMs || 8000 },
    );
  } catch (error) {
    if (error?.status === 404) return { found: false, clientRequestId, status: "unknown" };
    throw error;
  }
  if (data?.found === false) return { found: false, clientRequestId, status: "unknown" };
  return normalizeSimulatorChatStatus(data, clientRequestId);
}

export async function postSimulatorChatIntent({ simulatorSlug, clientRequestId, message }, options = {}) {
  const request = options.request || apiFetch;
  const slug = normalizeSimulatorSlug(simulatorSlug);
  const text = normalizeSimulatorChatText(message);
  if (!VALID_REQUEST_ID.test(String(clientRequestId || ""))) {
    throw chatIntentError("CHAT_INTENT_KEY_INVALID", "La identidad del mensaje no es válida.");
  }
  try {
    const data = await request(`/api/v1/simulator-chat/${encodeURIComponent(slug)}/messages`, {
      method: "POST",
      body: JSON.stringify({ message: text, clientRequestId }),
      timeoutMs: options.timeoutMs || 8000,
    });
    return normalizeSimulatorChatOutbound(data, { clientRequestId, simulatorSlug: slug });
  } catch (error) {
    if (shouldKeepSimulatorChatIntent(error)) {
      return { found: false, clientRequestId, status: "unknown" };
    }
    throw error;
  }
}

export function emitSimulatorChatIntent(socket, { simulatorSlug, clientRequestId, message }, options = {}) {
  const slug = normalizeSimulatorSlug(simulatorSlug);
  const text = normalizeSimulatorChatText(message);
  if (!VALID_REQUEST_ID.test(String(clientRequestId || ""))) {
    return Promise.reject(chatIntentError("CHAT_INTENT_KEY_INVALID", "La identidad del mensaje no es válida."));
  }
  if (!socket || typeof socket.emit !== "function") {
    return Promise.reject(chatIntentError("CHAT_SOCKET_UNAVAILABLE", "El chat en tiempo real no está disponible."));
  }
  const timeoutMs = Number.isFinite(options.timeoutMs) && options.timeoutMs > 0 ? options.timeoutMs : 12000;
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(chatIntentError("CHAT_ACK_TIMEOUT", "No se pudo confirmar la respuesta del chat a tiempo."));
    }, timeoutMs);
    socket.emit("simulator:message", { simulatorSlug: slug, clientRequestId, message: text }, (data) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        resolve(normalizeSimulatorChatOutbound(data, { clientRequestId, simulatorSlug: slug, requireSocketOk: true }));
      } catch (error) {
        reject(error);
      }
    });
  });
}

export function normalizeSimulatorChatOutbound(data, { clientRequestId, simulatorSlug, requireSocketOk = false } = {}) {
  if (data?.ok === false) {
    const error = chatIntentError(safeCode(data?.error?.code) || "CHAT_SEND_REJECTED", clean(data?.error?.message) || "No se pudo enviar el mensaje.");
    error.status = safeHttpStatus(data?.error?.status);
    throw error;
  }
  if (requireSocketOk && data?.ok !== true) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro devolvió una respuesta de chat que no se pudo verificar.");
  }
  const expectedKey = String(clientRequestId || "");
  const responseKey = String(data?.clientRequestId || "");
  const status = clean(data?.status).toLowerCase();
  if (!VALID_REQUEST_ID.test(expectedKey) || responseKey !== expectedKey || !BACKEND_STATUSES.has(status)
    || typeof data?.replayed !== "boolean" || !data?.simulator) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro devolvió una respuesta de chat que no se pudo verificar.");
  }
  if (normalizeSimulatorSlug(data.simulator.slug) !== normalizeSimulatorSlug(simulatorSlug)) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro devolvió una respuesta para otro simulador.");
  }
  const message = data?.message ? normalizeSimulatorChatMessage(data.message) : null;
  const messageId = validMessageId(data?.messageId || message?.id);
  if (status === "completed" && !messageId) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro no confirmó la identidad del mensaje enviado.");
  }
  if (message && message.id !== messageId) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro devolvió identidades de mensaje incompatibles.");
  }
  if (data?.replayed === false && status === "completed" && !message) {
    throw chatIntentError("CHAT_RESPONSE_INVALID", "Nitro no devolvió el mensaje de la primera ejecución.");
  }
  return Object.freeze({
    found: true,
    clientRequestId: expectedKey,
    status,
    messageId,
    message,
    errorCode: safeCode(data?.errorCode),
    replayed: data?.replayed === true,
  });
}

export function normalizeSimulatorChatStatus(data, expectedClientRequestId) {
  const clientRequestId = String(data?.clientRequestId || "");
  const status = clean(data?.status).toLowerCase();
  if (data?.found !== true || clientRequestId !== expectedClientRequestId || !BACKEND_STATUSES.has(status)) {
    throw chatIntentError("CHAT_STATUS_INVALID", "Nitro devolvió un estado de chat que no se pudo verificar.");
  }
  const messageId = validMessageId(data?.messageId);
  if (status === "completed" && !messageId) {
    throw chatIntentError("CHAT_STATUS_INVALID", "Nitro no confirmó la identidad del mensaje enviado.");
  }
  return Object.freeze({
    found: true,
    clientRequestId,
    status,
    messageId,
    errorCode: safeCode(data?.errorCode),
  });
}

export function normalizeSimulatorChatHistory(data, expectedSimulatorSlug) {
  const simulatorSlug = normalizeSimulatorSlug(expectedSimulatorSlug);
  if (normalizeSimulatorSlug(data?.simulator?.slug) !== simulatorSlug || !Array.isArray(data?.messages) || data.messages.length > 100) {
    throw chatIntentError("CHAT_HISTORY_INVALID", "Nitro devolvió un historial de chat que no se pudo verificar.");
  }
  return Object.freeze({
    simulator: Object.freeze({ slug: simulatorSlug }),
    messages: Object.freeze(dedupeSimulatorChatMessages(data.messages.map(normalizeSimulatorChatMessage))),
  });
}

export function normalizeSimulatorChatMessage(value) {
  const id = validMessageId(value?.id);
  const message = typeof value?.message === "string" ? value.message : "";
  if (!id || !message.trim() || message.length > 500) {
    throw chatIntentError("CHAT_MESSAGE_INVALID", "Nitro devolvió un mensaje de chat que no se pudo verificar.");
  }
  const displayName = clean(value?.author?.displayName).slice(0, 100) || "Piloto Nitro";
  const createdAt = safeDate(value?.createdAt);
  return Object.freeze({
    id,
    message,
    createdAt,
    author: Object.freeze({
      userId: Number.isSafeInteger(Number(value?.author?.userId)) && Number(value.author.userId) > 0
        ? Number(value.author.userId)
        : null,
      displayName,
    }),
  });
}

export function dedupeSimulatorChatMessages(messages = []) {
  const byId = new Map();
  for (const message of messages) {
    if (message?.id && !byId.has(message.id)) byId.set(message.id, message);
  }
  return [...byId.values()].slice(-80);
}

export function mergeSimulatorChatMessage(messages, message) {
  return dedupeSimulatorChatMessages([...(Array.isArray(messages) ? messages : []), message]);
}

export function normalizeSimulatorChatText(value) {
  const message = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!message) throw chatIntentError("CHAT_MESSAGE_REQUIRED", "El mensaje no puede estar vacío.");
  if (message.length > 500) throw chatIntentError("CHAT_MESSAGE_TOO_LONG", "El mensaje no puede superar 500 caracteres.");
  return message;
}

export function isSimulatorChatIntentBlocking(intent) {
  return Boolean(intent && !["completed", "failed"].includes(intent.status));
}

export function shouldKeepSimulatorChatIntent(error) {
  if (["CHAT_ACK_TIMEOUT", "CHAT_RESPONSE_INVALID", "NITRO_INVALID_RESPONSE"].includes(error?.code)) return true;
  if (Number(error?.status) >= 500) return true;
  return error?.status === undefined || error?.status === null;
}

export function runSimulatorChatSingleFlight(holder, factory) {
  if (!holder || typeof holder !== "object" || typeof factory !== "function") throw new TypeError("holder and factory are required");
  if (holder.current) return holder.current;
  // Install a sentinel synchronously before the first promise callback can run.
  let resolveOperation;
  let rejectOperation;
  const operation = new Promise((resolve, reject) => {
    resolveOperation = resolve;
    rejectOperation = reject;
  });
  holder.current = operation;
  Promise.resolve().then(factory).then(resolveOperation, rejectOperation);
  const release = () => {
    if (holder.current === operation) holder.current = null;
  };
  operation.then(release, release);
  return operation;
}

export function safeSimulatorChatTime(value, options = {}) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Hora no disponible";
  return new Intl.DateTimeFormat("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Bogota",
    ...options,
  }).format(date);
}

function isStoredIntent(value) {
  return Boolean(value && value.v === 1
    && VALID_DIGEST.test(value.actorBinding)
    && VALID_SIMULATOR_SLUG.test(value.simulatorSlug)
    && VALID_REQUEST_ID.test(value.clientRequestId)
    && VALID_DIGEST.test(value.contentDigest)
    && OPERATION_STATUSES.has(value.status)
    && (!value.messageId || VALID_MESSAGE_ID.test(value.messageId))
    && (!value.errorCode || /^[A-Z0-9_]{1,80}$/.test(value.errorCode))
    && safeDate(value.createdAt)
    && safeDate(value.updatedAt));
}

function storedIntent(intent) {
  return {
    v: 1,
    actorBinding: intent.actorBinding,
    simulatorSlug: normalizeSimulatorSlug(intent.simulatorSlug),
    clientRequestId: intent.clientRequestId,
    contentDigest: intent.contentDigest,
    status: intent.status,
    messageId: validMessageId(intent.messageId),
    errorCode: safeCode(intent.errorCode),
    createdAt: safeDate(intent.createdAt),
    updatedAt: safeDate(intent.updatedAt),
  };
}

function readStoredIntent(storage, storageKey, expectedActorBinding, expectedSimulatorSlug) {
  let current;
  try {
    current = JSON.parse(storage.getItem(storageKey) || "null");
  } catch {
    throw chatIntentError("CHAT_INTENT_STORAGE_INVALID", "El estado local del mensaje no se pudo verificar.");
  }
  if (!current) return null;
  if (!isStoredIntent(current)) throw chatIntentError("CHAT_INTENT_STORAGE_INVALID", "El estado local del mensaje no se pudo verificar.");
  if (current.actorBinding !== expectedActorBinding || current.simulatorSlug !== expectedSimulatorSlug) {
    throw chatIntentError("CHAT_INTENT_ACTOR_MISMATCH", "El mensaje pendiente pertenece a otra sesión o simulador.");
  }
  return storedIntent(current);
}

function chatStorageKey(actorBinding, simulatorSlug) {
  return `${STORAGE_PREFIX}:${actorBinding}:${simulatorSlug}`;
}

function storageFor(options) {
  let storage;
  try {
    storage = Object.prototype.hasOwnProperty.call(options, "storage") ? options.storage : globalThis.localStorage;
  } catch {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no puede conservar la identidad del mensaje.");
  }
  if (!storage || typeof storage.getItem !== "function" || typeof storage.setItem !== "function" || typeof storage.removeItem !== "function") {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no puede conservar la identidad del mensaje.");
  }
  return storage;
}

function legacyStorageFor(options) {
  let storage;
  try {
    if (Object.prototype.hasOwnProperty.call(options, "legacyStorage")) storage = options.legacyStorage;
    else if (Object.prototype.hasOwnProperty.call(options, "storage")) storage = null;
    else storage = globalThis.sessionStorage;
  } catch {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo comprobar mensajes pendientes de una sesión anterior.");
  }
  if (storage == null) return null;
  if (typeof storage.getItem !== "function" || typeof storage.removeItem !== "function") {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo comprobar mensajes pendientes de una sesión anterior.");
  }
  return storage;
}

function readOptionalStoredIntent(storage, storageKey, expectedActorBinding, expectedSimulatorSlug) {
  return storage ? readStoredIntent(storage, storageKey, expectedActorBinding, expectedSimulatorSlug) : null;
}

async function withSimulatorChatLock(actorBinding, simulatorSlug, options, staleValue, operation) {
  if (typeof options.guard === "function" && !options.guard()) return staleValue;
  const lockManager = lockManagerFor(options);
  const lockName = `${LOCK_PREFIX}:${actorBinding}:${simulatorSlug}`;
  try {
    return await lockManager.request(lockName, { mode: "exclusive", ifAvailable: true }, async (lock) => {
      if (!lock) throw chatIntentError("CHAT_INTENT_LOCK_BUSY", "Otro contexto está actualizando este mensaje. Vuelve a consultar su estado.");
      if (typeof options.guard === "function" && !options.guard()) return staleValue;
      return operation();
    });
  } catch (error) {
    if (error?.code) throw error;
    throw chatIntentError("CHAT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad del mensaje.");
  }
}

function lockManagerFor(options) {
  let lockManager;
  try {
    lockManager = Object.prototype.hasOwnProperty.call(options, "lockManager") ? options.lockManager : globalThis.navigator?.locks;
  } catch {
    throw chatIntentError("CHAT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad del mensaje.");
  }
  if (!lockManager || typeof lockManager.request !== "function") {
    throw chatIntentError("CHAT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad del mensaje.");
  }
  return lockManager;
}

function writeStoredIntent(storage, storageKey, intent) {
  try {
    storage.setItem(storageKey, JSON.stringify(intent));
  } catch {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo conservar la identidad del mensaje.");
  }
}

function removeStoredIntent(storage, storageKey) {
  try {
    storage.removeItem(storageKey);
  } catch {
    throw chatIntentError("CHAT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo cerrar el mensaje anterior.");
  }
}

function cryptoFor(options) {
  const cryptoApi = Object.prototype.hasOwnProperty.call(options, "cryptoApi") ? options.cryptoApi : globalThis.crypto;
  if (!cryptoApi?.subtle || typeof cryptoApi.randomUUID !== "function") {
    throw chatIntentError("CHAT_INTENT_CRYPTO_UNAVAILABLE", "El navegador no puede preparar una identidad segura para el mensaje.");
  }
  return cryptoApi;
}

async function sha256(value, cryptoApi) {
  const digest = await cryptoApi.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function normalizeSimulatorSlug(value) {
  const slug = clean(value).toLowerCase();
  if (!VALID_SIMULATOR_SLUG.test(slug)) throw chatIntentError("CHAT_SIMULATOR_INVALID", "El simulador no es válido.");
  return slug;
}

function validMessageId(value) {
  const id = String(value ?? "").trim();
  return VALID_MESSAGE_ID.test(id) ? id : null;
}

function safeCode(value) {
  const code = clean(value).toUpperCase();
  return /^[A-Z0-9_]{1,80}$/.test(code) ? code : null;
}

function safeHttpStatus(value) {
  const status = Number(value);
  return Number.isInteger(status) && status >= 400 && status <= 599 ? status : undefined;
}

function validNow(value) {
  const result = safeDate(value);
  if (!result) throw chatIntentError("CHAT_CLOCK_INVALID", "No fue posible fechar la solicitud del mensaje.");
  return result;
}

function safeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function clean(value) {
  return String(value ?? "").trim();
}

function chatIntentError(code, message) {
  return Object.assign(new Error(message || code), { code });
}
